import React, { useState, useEffect } from "react";
import { Link, useNavigate, NavLink, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../../config/config';
import { FiSearch, FiMapPin, FiDollarSign } from 'react-icons/fi';
import Popupform from '../../Popupform/Popupform';

const Engineering = () => {
    const { courseName } = useParams();
    const navigate = useNavigate();

    const [tableData, setTableData] = useState([]);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [showPopup, setShowPopup] = useState(false);

    // Fetch college data
    useEffect(() => {
        const fetchCollegeData = async () => {
            try {
                const response = await axios.get(
                    `${config.API_BASE_URL}/api/admin/get_collegespageByCourse/${courseName}`
                );
                const fetchedData = response.data.collegesByCourseName || [];
                setTableData(fetchedData);
            } catch (error) {
                console.error("Error fetching college data:", error);
            }
        };

        if (courseName) {
            fetchCollegeData();
        }
    }, [courseName]);

    // Handle search input
    const handleInputChange = async (e) => {
        const value = e.target.value;
        setQuery(value);

        if (value.length > 1) {
            try {
                const response = await axios.get(`${config.API_BASE_URL}/api/admin/search_api`, {
                    params: {
                        courseName: value,
                        collegeName: value,
                        state: value,
                    },
                });
                setSuggestions(response.data.searchAPIData || []);
            } catch (err) {
                console.error("Search error:", err);
            }
        } else {
            setSuggestions([]);
        }
    };

    // Pagination
    const totalPages = Math.ceil(tableData.length / itemsPerPage);
    const paginatedData = tableData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleOpenPopup = (e) => {
        e.preventDefault();
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Banner */}
            <div className="relative w-full min-h-[500px] flex flex-col justify-start bg-gray-900 mb-8 -mt-[130px] lg:-mt-[180px] pt-[160px] lg:pt-[220px] z-20">
                {/* Background & Overlay */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=2070&auto=format&fit=crop"
                        alt="Education Background"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-slate-900/80 to-blue-900/90"></div>
                </div>

                <div className="container relative z-10 mx-auto px-4 text-center">
                    <div className="max-w-4xl mx-auto">
                        <span className="inline-block py-1 px-3 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-300 text-xs font-bold uppercase tracking-wider mb-4 backdrop-blur-sm">
                            Discover Your Future
                        </span>
                        <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight drop-shadow-xl">
                            Search <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-300">{courseName}</span> Colleges, Courses & Exams
                        </h1>
                        <p className="text-gray-300 text-lg md:text-xl font-light max-w-2xl mx-auto mb-8">
                            Unlock your potential with the best institutions. Compare fees, placements, and rankings to make the right choice.
                        </p>

                        {/* Search Bar */}
                        <div className="max-w-2xl mx-auto relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                                <FiSearch className="text-gray-400 group-focus-within:text-orange-500 transition-colors" size={20} />
                            </div>
                            <input
                                type="text"
                                className="w-full pl-12 pr-4 py-4 rounded-full bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-orange-500/30 shadow-2xl transition-all"
                                placeholder={`Search for ${courseName} colleges, cities, or changes...`}
                                value={query}
                                onChange={handleInputChange}
                            />

                            {/* Suggestions Dropdown */}
                            {suggestions.length > 0 && (
                                <ul className="absolute top-full left-0 right-0 bg-white text-gray-800 rounded-2xl shadow-2xl mt-2 max-h-80 overflow-y-auto z-50 py-2 border border-gray-100 text-left">
                                    {suggestions.map((item, idx) => (
                                        <li key={idx} className="border-b border-gray-50 last:border-0">
                                            <Link
                                                to={`/college/${item._id}`}
                                                className="block px-6 py-3 hover:bg-orange-50 transition"
                                            >
                                                <strong className="text-gray-900 block">{item.collegeName}</strong>
                                                <span className="text-gray-500 text-xs">{item.city}, {item.state}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Quick Links */}
                        <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
                            <span className="text-gray-400 text-xs uppercase font-bold tracking-wider mr-2">Trending:</span>
                            {['IIT Delhi', 'IIM Ahmedabad', 'BITS Pilani'].map(tag => (
                                <button key={tag} className="bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 px-3 py-1 rounded-full text-xs transition">
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Top Colleges Table */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-gray-800 mb-8">Top {courseName} Colleges</h2>

                    {paginatedData.length > 0 ? (
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                            <div className="overflow-x-auto">
                                {/* Desktop Header */}
                                <div className="hidden md:grid grid-cols-12 gap-4 p-5 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50">
                                    <div className="col-span-1 text-center">Rank</div>
                                    <div className="col-span-5">College</div>
                                    <div className="col-span-3">Application Deadline</div>
                                    <div className="col-span-3">Course Fees</div>
                                </div>

                                {paginatedData.map((item, index) => (
                                    <React.Fragment key={index}>
                                        {/* Mobile Card View */}
                                        <div className="md:hidden bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-4 relative overflow-hidden">
                                            <div className="flex items-start gap-4 mb-4">
                                                {/* Rank Badge */}
                                                <div className="absolute top-0 left-0 bg-yellow-400 text-white text-[10px] font-bold px-3 py-1 rounded-br-lg shadow-sm z-10">
                                                    #{(currentPage - 1) * itemsPerPage + index + 1}
                                                </div>

                                                {/* Image */}
                                                <img
                                                    src={`${config.API_BASE_URL}/${item.image}`}
                                                    alt={item.collegeName}
                                                    className="w-16 h-16 rounded-lg object-contain border border-gray-100 bg-white mt-4 flex-shrink-0"
                                                />

                                                {/* Content */}
                                                <div className="mt-4 flex-1">
                                                    <NavLink
                                                        to={`/college/${item._id}`}
                                                        className="font-bold text-gray-900 leading-tight text-sm block mb-1"
                                                    >
                                                        {item.collegeName}
                                                    </NavLink>
                                                    <p className="text-xs text-gray-500 mb-2">{item.subheading}</p>

                                                    <div className="flex items-center gap-1 text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded-md w-fit">
                                                        <span>Admissions Open</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-3 mb-3">
                                                <div>
                                                    <p className="text-[10px] text-gray-400 uppercase font-semibold">First Year Fees</p>
                                                    <p className="text-sm font-bold text-gray-800">
                                                        ₹ {item.coursesDeatils && item.coursesDeatils.length > 0 ? item.coursesDeatils[0].fees : "N/A"}
                                                    </p>
                                                    <p className="text-[10px] text-gray-400">
                                                        {item.coursesDeatils && item.coursesDeatils.length > 0 ? item.coursesDeatils[0].courses : ""}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-gray-400 uppercase font-semibold">Application Deadline</p>
                                                    <p className="text-xs font-bold text-gray-800">
                                                        {item.coursesDeatils && item.coursesDeatils.length > 0 ? item.coursesDeatils[0].applicationDate : "N/A"}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleOpenPopup}
                                                    className="flex-1 bg-orange-50 text-orange-600 border border-orange-200 text-xs font-bold py-2.5 rounded-lg hover:bg-orange-100 transition"
                                                >
                                                    Apply Now
                                                </button>
                                                <NavLink
                                                    to={`/college/${item._id}`}
                                                    className="flex-1 bg-gray-50 text-gray-600 border border-gray-200 text-xs font-bold py-2.5 rounded-lg hover:bg-gray-100 transition text-center"
                                                >
                                                    View Details
                                                </NavLink>
                                            </div>
                                        </div>

                                        {/* Desktop Row View */}
                                        <div className="hidden md:grid grid-cols-12 gap-4 p-5 border-b border-gray-100 hover:bg-blue-50/50 transition items-center">
                                            <div className="col-span-1 text-center font-bold text-gray-600">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </div>
                                            <div className="col-span-5 flex items-center gap-4">
                                                <img
                                                    src={`${config.API_BASE_URL}/${item.image}`}
                                                    alt={item.collegeName}
                                                    className="w-12 h-12 object-contain rounded border border-gray-100 bg-white"
                                                />
                                                <div>
                                                    <NavLink
                                                        to={`/college/${item._id}`}
                                                        className="font-bold text-gray-800 hover:text-brand-orange transition text-sm block"
                                                    >
                                                        {item.collegeName}
                                                    </NavLink>
                                                    <p className="text-xs text-gray-500 mt-0.5">{item.subheading}</p>
                                                    <button
                                                        onClick={handleOpenPopup}
                                                        className="mt-1 text-xs text-brand-orange font-bold hover:underline"
                                                    >
                                                        Apply Now →
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="col-span-3 text-gray-600 text-sm">
                                                {item.coursesDeatils && item.coursesDeatils.length > 0
                                                    ? item.coursesDeatils[0].applicationDate
                                                    : "N/A"}
                                            </div>
                                            <div className="col-span-3">
                                                <div className="text-green-600 font-bold text-sm">
                                                    ₹ {item.coursesDeatils && item.coursesDeatils.length > 0
                                                        ? item.coursesDeatils[0].fees
                                                        : "N/A"}
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    {item.coursesDeatils && item.coursesDeatils.length > 0
                                                        ? item.coursesDeatils[0].courses
                                                        : ""}
                                                </p>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-center gap-3">
                                    <label className="text-sm font-semibold text-gray-700">Page:</label>
                                    <select
                                        value={currentPage}
                                        onChange={(e) => setCurrentPage(Number(e.target.value))}
                                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                                    >
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <option key={page} value={page}>
                                                {page}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
                            <p className="text-gray-500 text-lg">No colleges found for {courseName}</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Popup Modal - You can implement this later */}
            {/* Popup Modal */}
            {showPopup && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999] p-4"
                    onClick={handleClosePopup}
                >
                    <div
                        className="bg-white rounded-2xl w-full max-w-lg relative max-h-[85vh] overflow-y-auto shadow-2xl custom-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={handleClosePopup}
                            className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-gray-600 hover:text-gray-900 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Form Content with Padding */}
                        <div className="p-8">
                            <Popupform />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Engineering;
